import type { SnapProgressMetadata } from "~/types/report-progress";

/**
 * Ordenação single-flight, POR RELATÓRIO, dos saves de parcial (épico #21124).
 *
 * Um parcial cumulativo chega a ~18 MB: encriptar e fazer o `PUT` demora, e dois
 * snapshots do mesmo relatório podem se sobrepor. Sem ordenação, o `PUT` do
 * snapshot 6 pode comitar DEPOIS do 7 e regredir o relatório salvo.
 *
 * Garantias (todas testadas):
 *
 * - **relatórios distintos salvam em paralelo** — o estado é por `reportId`;
 * - **o mesmo relatório nunca comita fora de ordem** — um save por vez;
 * - **coalescing** — enquanto um save roda, snapshots novos substituem o
 *   enfileirado; sobrevive o mais NOVO. Snapshot e progresso são coalescidos
 *   como UMA unidade (nunca o snapshot de uma sequência com o progresso de
 *   outra);
 * - **terminal bloqueia NOVOS saves, mas drena o que já estava enfileirado**
 *   — `markReportTerminal` para de aceitar parciais dali em diante, mas o
 *   último snapshot coalescido (se um save já estava em voo) ainda é
 *   enviado antes de parar; o guard de status no servidor
 *   (`_reconcile_report_status`) é quem decide se esse save tardio é aceito
 *   (relatório terminal ainda incompleto) ou rejeitado (já correto);
 * - **sequência duplicada/atrasada é no-op idempotente**;
 * - **falha não avança a sequência "durably saved"**, então o próximo parcial
 *   (ou o replay da reconexão) tenta de novo.
 *
 * Este módulo é estado puro de ORDENAÇÃO — não conhece criptografia, rede nem
 * conteúdo de relatório.
 */
export interface PartialSaveJob {
  reportId: string;
  sequence: number;
  /** Snapshot cumulativo canônico, em texto claro, apenas em memória. */
  snapshot: unknown;
  /** Progresso associado A ESTA sequência — coalescido junto do snapshot. */
  progress: SnapProgressMetadata | null;
}

interface ReportSaveState {
  running: boolean;
  queued: PartialSaveJob | null;
  /** Maior sequência efetivamente comitada no servidor. */
  savedSequence: number;
  /** Maior sequência aceita para salvar (em voo ou enfileirada). */
  acceptedSequence: number;
  terminal: boolean;
}

type SaveRunner = (job: PartialSaveJob) => Promise<void>;

const states = new Map<string, ReportSaveState>();

// Deliberately separate from `acceptedSequence` above: that one only advances
// once `enqueuePartialSave`'s async mutationFn actually runs, so gating
// RENDER on it would race the mutation's microtask. This one updates in the
// same synchronous tick the frame arrives in.
const renderedSequences = new Map<string, number>();

function stateFor(reportId: string): ReportSaveState {
  let state = states.get(reportId);
  if (!state) {
    state = {
      running: false,
      queued: null,
      savedSequence: -1,
      acceptedSequence: -1,
      terminal: false,
    };
    states.set(reportId, state);
  }
  return state;
}

/** Somente para testes: zera todo o estado de ordenação. */
export function resetPartialSaveQueue(): void {
  states.clear();
  renderedSequences.clear();
}

/**
 * Synchronous claim for the RENDER step: true (and marks it as rendered) if
 * `sequence` is newer than the last rendered one and the report isn't
 * terminal. Idempotent — repeating the same or a lower sequence never regresses.
 */
export function claimPartialSequenceForRender(reportId: string, sequence: number): boolean {
  if (states.get(reportId)?.terminal) return false;
  const last = renderedSequences.get(reportId) ?? -1;
  if (sequence <= last) return false;
  renderedSequences.set(reportId, sequence);
  return true;
}

/** Maior sequência que já foi COMITADA no servidor para este relatório. */
export function lastSavedSequence(reportId: string): number {
  return states.get(reportId)?.savedSequence ?? -1;
}

/**
 * Marca o relatório como terminal: bloqueia novos parciais dali em diante.
 *
 * Chamado quando o resultado final chega, para que nenhum save intermediário
 * atrasado sobrescreva sucesso ou erro — mas NÃO descarta um snapshot já
 * enfileirado (`state.queued`). Regression (staging, LinkedIn — relatórios
 * terminando com poucas ou nenhuma seção salva mesmo a Snap tendo entregue
 * tudo): como as etapas granulares chegam quase juntas (mais rápido que um
 * ciclo de encriptar+PUT), quase sempre HÁ um snapshot coalescido mais novo
 * esperando no exato momento em que o sinal de finalização tradicional
 * chega — descartá-lo aqui jogava fora o conteúdo mais completo que o
 * relatório já tinha. `enqueuePartialSave`'s `while` já para de aceitar
 * NOVOS jobs assim que `terminal` é true; o que já estava enfileirado
 * continua sendo drenado (uma última vez) pelo loop em andamento. Um save
 * que chegue depois do terminal no servidor é rejeitado normalmente pela
 * guarda de status (`_reconcile_report_status`) quando o relatório já está
 * completo — nunca regride conteúdo correto.
 */
export function markReportTerminal(reportId: string): void {
  if (!reportId) return;
  const state = stateFor(reportId);
  state.terminal = true;
}

/** Descarta o estado de um relatório (após terminal + limpeza da UI). */
export function forgetReport(reportId: string): void {
  states.delete(reportId);
}

/**
 * Enfileira um snapshot cumulativo para salvar, respeitando a ordem.
 *
 * `runner` executa o save de fato (encriptar + `PUT`). Resolve quando ESTE job
 * — ou o job que o superou por coalescing — terminou de ser processado.
 */
export async function enqueuePartialSave(
  job: PartialSaveJob,
  runner: SaveRunner,
): Promise<void> {
  if (!job.reportId) return;
  const state = stateFor(job.reportId);

  // Terminal vence sempre.
  if (state.terminal) return;

  // Duplicado ou atrasado: no-op idempotente.
  if (job.sequence <= state.acceptedSequence) return;
  state.acceptedSequence = job.sequence;

  if (state.running) {
    // Coalescing: o snapshot é cumulativo, então o mais novo já contém tudo o
    // que o enfileirado continha. Snapshot e progresso trocam JUNTOS.
    state.queued = job;
    return;
  }

  state.running = true;
  let current: PartialSaveJob | null = job;
  try {
    while (current) {
      const running: PartialSaveJob = current;
      try {
        await runner(running);
        // Só um save bem-sucedido avança a sequência durável.
        if (running.sequence > state.savedSequence) {
          state.savedSequence = running.sequence;
        }
      } catch {
        // Falha preservada como falha: a sequência durável NÃO avança, então o
        // próximo parcial (ou o replay) reenvia. O erro já foi tratado pelo
        // runner (toast + createErrorHandler); aqui só não propagamos para não
        // derrubar a fila do relatório.
      }
      // Drena o que já estava enfileirado mesmo se `terminal` virou true
      // durante este `await` — ver docstring de `markReportTerminal`. Uma
      // vez drenado, nada mais pode reenfileirar (o guard de entrada de
      // `enqueuePartialSave` já rejeita novos jobs quando terminal), então
      // este loop sempre converge.
      current = state.queued;
      state.queued = null;
    }
  } finally {
    state.running = false;
  }
}
